import { skillsReady } from '../../skills/loader.js';
import { claude, requireKey } from '../api.js';
import { $, assembled, assembledForPage, createTabSwitcher, finalizeFrame, renderDevSw, showErr, writeFrame } from '../dom.js';
import { KEEP_TOKENS_SCHEMA, VISION_REFINE_SCHEMA } from '../prompts/constants.js';
import { syncFlatTokens, tokensToMap } from '../prompts/context.js';
import { renderBrandPanel } from '../screens/concept.js';
import { renderChat } from '../screens/chat.js';
import { curPage, ST } from '../state.js';
import { val } from './export.js';
import { buildUploadImgMap } from './helpers.js';
import { buildSect } from './pipeline.js';

// ── DESIGN PREVIEW ────────────────────────────────────────────────
// Design shell (CSS variables), 3-section preview on real page, chat refinement.

// genDesignPreview, renderDesign are DOM-dependent — become Vue components.

let designBusy = false;

function genDesignPreview() {
  requireKey(() => {
    void GenDesignPreview();
  });
}

// buildDesignShell is pure logic (no DOM) — direct migration to utility function.

/**
 * Build the HTML shell (head + foot) with CSS variables from design tokens.
 * @param {import('../types.ts').Concept} c
 * @returns {{ head: string, foot: string }}
 */
function buildDesignShell(c) {
  const cl = c.colors || {};
  const fn = c.fonts || {};
  const dt = c.designTokens;
  const hf = fn.heading || 'DM Sans';
  const bf = fn.body || 'DM Sans';
  const gfUrl = `https://fonts.googleapis.com/css2?family=${hf.replace(
    / /g,
    '+',
  )}:wght@400;500;600;700;800&family=${bf.replace(/ /g, '+')}:wght@400;500;600&display=swap`;

  // tokensToMap: [{key,value,...}] → { key: {value,...} } — keys are kebab-case per schema
  const cc = tokensToMap(dt?.colors);
  const c_ = k => val(cc[k]?.value, cl[k]);

  const ce = tokensToMap(dt?.effects);

  const primary = c_('primary') || '#6366f1';
  const primaryDark = c_('primary-dark') || '#4f46e5';
  const primaryLight = c_('primary-light') || primary;
  const accent = c_('accent') || '#8b5cf6';
  const secondary = c_('secondary') || accent;
  const bg = c_('bg') || '#fff';
  const bgAlt = c_('bg-alt') || '#f8fafc';
  const surface = c_('surface') || '#fff';
  const surfaceHover = c_('surface-hover') || surface;
  const text = c_('text') || '#0f172a';
  const textMuted = c_('text-muted') || '#64748b';
  const border = c_('border') || '#e2e8f0';
  const gradFrom = c_('gradient-from') || primary;
  const gradTo = c_('gradient-to') || accent;
  const radius = ce['radius']?.value || '12px';
  const radiusLg = ce['radius-lg']?.value || '20px';
  const shadow = ce['shadow']?.value || '0 4px 24px rgba(0,0,0,.06)';
  const shadowLg = ce['shadow-lg']?.value || '0 16px 48px rgba(0,0,0,.1)';

  const scaleDisplay = dt.typography.scale.display;
  const scaleSmall = dt.typography.scale.small;

  const animEasing = dt.animation.easing;
  const animFast = dt.animation.duration.fast;
  const animMedium = dt.animation.duration.medium;
  const animSlow = dt.animation.duration.slow;

  let head = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${ST.existingScrape?.title || c.siteName || 'Site'}</title>
${
  ST.existingScrape?.metaDescription
    ? `\n<meta name="description" content="${ST.existingScrape.metaDescription.replace(/"/g, '&quot;')}">`
    : ''
}
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${gfUrl}" rel="stylesheet">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/normalize.css/normalize.css">
<script src="https://cdn.jsdelivr.net/npm/motion@12/dist/motion.js"></script>
<style data-shell>
/* === DESIGN TOKENS (auto-generated) === */
:root{
  --primary:${primary};--primary-dark:${primaryDark};--primary-light:${primaryLight};
  --accent:${accent};--secondary:${secondary};
  --bg:${bg};--bg-alt:${bgAlt};--surface:${surface};--surface-hover:${surfaceHover};
  --text:${text};--text-muted:${textMuted};--border:${border};
  --grad-from:${gradFrom};--grad-to:${gradTo};
  --font-h:'${hf}',system-ui,sans-serif;--font-b:'${bf}',system-ui,sans-serif;
  --radius:${radius};--radius-lg:${radiusLg};
  --shadow:${shadow};
  --shadow-lg:${shadowLg};
  --display:${scaleDisplay};--small:${scaleSmall};
  --ease:${animEasing};--duration-fast:${animFast};--duration-medium:${animMedium};--duration-slow:${animSlow};
}
html{scroll-behavior:smooth}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:var(--font-b);background:var(--bg);color:var(--text);line-height:1.6;-webkit-font-smoothing:antialiased;overflow-x:hidden}
h1,h2,h3,h4,h5{font-family:var(--font-h);margin:0}
svg:not([width]){width:24px;height:24px}svg{flex-shrink:0}
.reveal{opacity:0;transform:translateY(24px)}
@media(prefers-reduced-motion:reduce){.reveal{opacity:1!important;transform:none!important}}
section{transition:opacity .15s ease}
.sf-selected{outline:2px solid var(--primary);outline-offset:-2px;position:relative}
.sf-selected::after{content:attr(id);position:absolute;top:4px;right:4px;background:var(--primary);color:#fff;font-size:11px;padding:2px 8px;border-radius:4px;font-family:system-ui;z-index:9999}
.sf-editing{animation:sf-pulse 1.5s ease-in-out infinite}
@keyframes sf-pulse{0%,100%{opacity:1}50%{opacity:.85}}
.video-facade{position:relative;width:100%;aspect-ratio:16/9;background-size:cover;background-position:center;border-radius:var(--radius-lg);overflow:hidden;cursor:pointer}
.video-facade iframe{position:absolute;inset:0;width:100%;height:100%;border:none;border-radius:var(--radius-lg)}
.video-overlay{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(0,0,0,.25);transition:background .3s}
.video-facade:hover .video-overlay{background:rgba(0,0,0,.45)}
.play-btn{width:72px;height:72px;border-radius:50%;border:none;background:var(--primary);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:transform .2s,box-shadow .2s;box-shadow:0 4px 20px rgba(0,0,0,.3)}
.play-btn:hover{transform:scale(1.1)}
.video-caption{color:#fff;margin-top:12px;text-shadow:0 1px 4px rgba(0,0,0,.5)}
@media(max-width:768px){.play-btn{width:56px;height:56px}}
.sf-hl{position:absolute;pointer-events:none;border:2px dashed rgba(99,102,241,.4);border-radius:4px;z-index:99998;opacity:0;transition:top .2s ease,left .2s ease,width .2s ease,height .2s ease,opacity .15s ease,border-color .15s ease}
.sf-hl-visible{opacity:1}
.sf-hl-editing{border-style:solid;border-color:var(--primary,#6366f1);background:rgba(99,102,241,.03)}
.sf-hl-dark{border-color:rgba(255,255,255,.5)}
.sf-hl-dark.sf-hl-editing{border-color:rgba(255,255,255,.8);background:rgba(255,255,255,.05)}
.sf-hl-saved{border-color:#10b981!important;background:rgba(16,185,129,.06)}
.sf-hl::before{content:attr(data-label);position:absolute;top:-20px;left:0;font-size:10px;font-family:system-ui,sans-serif;padding:1px 6px;border-radius:3px;background:rgba(99,102,241,.8);color:#fff;opacity:0;transition:opacity .15s}
.sf-hl-visible::before{opacity:1}
.sf-hl-editing::before{background:var(--primary,#6366f1)}
.sf-hl-dark::before{background:rgba(255,255,255,.7);color:#1a1a2e}
.sf-hl-saved::before{content:'saved ✓';background:#10b981}
[contenteditable="true"]{cursor:text;outline:none}
@media(pointer:coarse){.sf-hl{display:none!important}}
</style>
`;

  head += '</head>\n<body>';

  // NOTE: \u003c / \u003e are unicode escapes for < / > to prevent the browser
  // HTML parser from treating these as real script tags inside the JS source.
  const foot = `
\u003cscript\u003e
// Reveal animations
(() => {
  if (window.matchMedia('(prefers-reduced-motion:reduce)').matches) {
    document.querySelectorAll('.reveal').forEach(el => { el.style.opacity='1'; el.style.transform='none'; });
    return;
  }
  if (typeof Motion !== 'undefined' && Motion.inView) {
    Motion.inView('.reveal', el => {
      Motion.animate(el, {opacity:[0,1], y:[24,0]}, {duration:0.5, easing:[0.16,1,0.3,1]});
    }, {margin:'-10% 0px'});
  } else {
    document.querySelectorAll('.reveal').forEach(el => {
      const obs = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          entries[0].target.style.opacity = '1';
          entries[0].target.style.transform = 'none';
          entries[0].target.style.transition = 'opacity .7s ease,transform .7s ease';
          obs.unobserve(entries[0].target);
        }
      }, {threshold:.1});
      obs.observe(el);
    });
  }
})();

// Sticky header
window.addEventListener('scroll', () => {
  const hdr = document.querySelector('#header,header');
  if (hdr) hdr.classList.toggle('scrolled', window.scrollY > 20);
}, {passive:true});

// Broken image placeholders
document.querySelectorAll('img').forEach(img => {
  img.addEventListener('error', () => {
    img.style.display = 'none';
    const ph = document.createElement('div');
    ph.style.cssText = 'width:100%;height:100%;min-height:200px;background:linear-gradient(135deg,var(--grad-from,#6366f1),var(--grad-to,#8b5cf6));border-radius:var(--radius-lg,12px);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.3);font-size:3rem';
    ph.textContent = '\\u25A8';
    img.parentNode.insertBefore(ph, img);
  });
});

// Form submissions
document.querySelectorAll('form[action="/api/form-submit"]').forEach(form => {
  form.addEventListener('submit', e => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"],button:not([type]),input[type="submit"]');
    const origText = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sending\\u2026'; }
    const prev = form.parentNode.querySelector('.sf-form-msg');
    if (prev) prev.remove();
    fetch(form.action, {method:'POST', body:new FormData(form)})
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          const msg = document.createElement('div');
          msg.className = 'sf-form-msg';
          msg.style.cssText = 'padding:16px;margin-top:16px;background:var(--primary,#10b981);color:#fff;border-radius:var(--radius,8px);text-align:center;font-weight:600';
          msg.textContent = d.message || 'Thank you!';
          form.parentNode.insertBefore(msg, form.nextSibling);
          form.reset();
        } else { throw new Error(d.error || 'Submission failed'); }
      })
      .catch(err => {
        const msg = document.createElement('div');
        msg.className = 'sf-form-msg';
        msg.style.cssText = 'padding:16px;margin-top:16px;background:#ef4444;color:#fff;border-radius:var(--radius,8px);text-align:center;font-weight:600';
        msg.textContent = err.message || 'Something went wrong. Please try again.';
        form.parentNode.insertBefore(msg, form.nextSibling);
        setTimeout(() => msg.remove(), 5000);
      })
      .finally(() => { if (btn) { btn.disabled = false; btn.textContent = origText; } });
  });
});

// Video facades
document.querySelectorAll('.video-facade').forEach(el => {
  el.addEventListener('click', () => {
    const id = el.getAttribute('data-video-id');
    const prov = el.getAttribute('data-provider');
    if (!id || el.querySelector('iframe')) return;
    const src = prov === 'vimeo'
      ? 'https://player.vimeo.com/video/' + id + '?autoplay=1'
      : 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
    const ifr = document.createElement('iframe');
    ifr.src = src;
    ifr.setAttribute('allow', 'accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture');
    ifr.setAttribute('allowfullscreen', '');
    ifr.setAttribute('title', 'Video');
    const ov = el.querySelector('.video-overlay');
    if (ov) ov.remove();
    el.appendChild(ifr);
    el.style.cursor = 'default';
    el.style.pointerEvents = 'none';
    ifr.style.pointerEvents = 'auto';
  });
});

// Inline text editing
(() => {
let _editMode = false;
const _hl = document.createElement('div');
_hl.className = 'sf-hl';
document.body.appendChild(_hl);
let _hlTarget = null;
let _editing = null;
let _editSnapshot = '';
let _hideTimer = null;
const _editableTags = ['H1','H2','H3','H4','H5','P','SPAN','A','BUTTON','LI','BLOCKQUOTE','FIGCAPTION','LABEL'];

const _isEditable = (el) => {
  if (!_editableTags.includes(el.tagName)) return false;
  if (el.closest('nav,.sf-hl,script,style,svg,img,input,textarea,select')) return false;
  if (el.children.length > 3) return false;
  if (el.offsetParent === null) return false;
  const t = el.textContent;
  return t && t.trim().length > 0 && t.length < 2000;
};

const _labelForTag = (tag) => {
  const map = {H1:'heading',H2:'heading',H3:'heading',P:'text',A:'link',BUTTON:'button',SPAN:'label',LI:'item',BLOCKQUOTE:'quote'};
  return map[tag] || 'text';
};

const _positionHl = (target) => {
  const rect = target.getBoundingClientRect();
  const pad = 4;
  _hl.style.top = (rect.top + window.scrollY - pad) + 'px';
  _hl.style.left = (rect.left + window.scrollX - pad) + 'px';
  _hl.style.width = (rect.width + pad * 2) + 'px';
  _hl.style.height = (rect.height + pad * 2) + 'px';
};

const _isDarkBg = (bg) => {
  const m = bg.match(/\\d+/g);
  if (!m || m.length < 3) return false;
  return (parseInt(m[0]) * 299 + parseInt(m[1]) * 587 + parseInt(m[2]) * 114) / 1000 < 128;
};

const _findSectionStyle = (sectionId) => {
  const pat = new RegExp('#' + sectionId + '[\\\\s{.,:\\\\[>~+}]');
  const styles = document.querySelectorAll('style:not([data-shell])');
  for (const style of styles) {
    if (pat.test(style.textContent)) return style;
  }
  return null;
};

const _finishEdit = () => {
  if (!_editing) return;
  const el = _editing;
  el.contentEditable = 'false';
  _hl.classList.remove('sf-hl-editing');
  _editing = null;
  if (el.innerHTML === _editSnapshot) {
    window.parent.postMessage({ type: 'sf-inline-edit', active: false }, '*');
    return;
  }
  const section = el.closest('section[id]');
  if (section) {
    _hl.classList.add('sf-hl-saved');
    setTimeout(() => _hl.classList.remove('sf-hl-saved'), 600);
    const style = _findSectionStyle(section.id);
    const payload = (style ? style.outerHTML + '\\n' : '') + section.outerHTML;
    window.parent.postMessage({ type: 'sf-text-edit', sectionId: section.id, sectionHtml: payload }, '*');
  }
  window.parent.postMessage({ type: 'sf-inline-edit', active: false }, '*');
};

document.addEventListener('mousemove', (e) => {
  if (!_editMode || _editing) return;
  const el = document.elementFromPoint(e.clientX, e.clientY);
  if (!el) return;
  const target = el.closest(_editableTags.join(','));
  if (!target || !_isEditable(target)) {
    if (!_hideTimer) {
      _hideTimer = setTimeout(() => {
        _hl.classList.remove('sf-hl-visible');
        _hlTarget = null;
        _hideTimer = null;
      }, 80);
    }
    return;
  }
  if (_hideTimer) { clearTimeout(_hideTimer); _hideTimer = null; }
  if (target === _hlTarget) return;
  _hlTarget = target;
  _positionHl(target);
  const bg = getComputedStyle(target.closest('section') || document.body).backgroundColor;
  _hl.classList.toggle('sf-hl-dark', _isDarkBg(bg));
  _hl.dataset.label = _labelForTag(target.tagName);
  _hl.classList.add('sf-hl-visible');
});

document.addEventListener('scroll', () => {
  if (_hlTarget) _positionHl(_hlTarget);
}, {passive:true});

document.addEventListener('mouseleave', () => {
  if (!_editing) { _hl.classList.remove('sf-hl-visible'); _hlTarget = null; }
});

document.addEventListener('click', (e) => {
  if (!_editMode) return;
  const target = _hlTarget;
  if (_editing && (!target || !_isEditable(target) || target !== _editing)) {
    _finishEdit();
    if (!target || !_isEditable(target)) return;
  }
  if (!target || !_isEditable(target)) return;
  e.preventDefault();
  e.stopPropagation();
  _editing = target;
  _editSnapshot = target.innerHTML;
  target.contentEditable = 'true';
  _hl.classList.add('sf-hl-editing');
  if (document.caretRangeFromPoint) {
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (range) { const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); }
  } else { target.focus(); }
  window.parent.postMessage({ type: 'sf-inline-edit', active: true }, '*');
});

document.addEventListener('focusout', () => {
  if (!_editing) return;
  const wasEditing = _editing;
  setTimeout(() => { if (_editing === wasEditing) _finishEdit(); }, 100);
});

document.addEventListener('paste', (e) => {
  if (!_editing) return;
  e.preventDefault();
  document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
});

document.addEventListener('keydown', (e) => {
  if (!_editing) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    _editing.innerHTML = _editSnapshot;
    _editing.contentEditable = 'false';
    _hl.classList.remove('sf-hl-editing');
    _editing = null;
    window.parent.postMessage({ type: 'sf-inline-edit', active: false }, '*');
    return;
  }
  if (e.key === 'Tab') {
    e.preventDefault();
    _finishEdit();
    const all = [...document.querySelectorAll(_editableTags.join(','))].filter(_isEditable);
    const idx = all.indexOf(_hlTarget);
    const next = all[e.shiftKey ? idx - 1 : idx + 1];
    if (next) { _hlTarget = next; _positionHl(next); _hl.classList.add('sf-hl-visible'); next.click(); }
  }
});

// Click-to-select section
document.addEventListener('click', (e) => {
  if (_editing) return;
  const sect = e.target.closest('section[id]');
  if (!sect) return;
  if (e.target.closest('a,button,input,textarea,select')) return;
  document.querySelectorAll('.sf-selected').forEach(el => el.classList.remove('sf-selected'));
  sect.classList.add('sf-selected');
  window.parent.postMessage({ type: 'sf-select', id: sect.id }, '*');
});

// Toggle edit mode from parent
window.addEventListener('message', (e) => {
  if (e.data?.type === 'sf-toggle-edit') {
    _editMode = !!e.data.enabled;
    if (!_editMode) {
      if (_editing) {
        _editing.innerHTML = _editSnapshot;
        _editing.contentEditable = 'false';
        _editing = null;
      }
      _hl.classList.remove('sf-hl-visible', 'sf-hl-editing');
      _hlTarget = null;
    }
  }
});
})();

// Re-trigger resize after layout so carousels/sliders init with correct dimensions
requestAnimationFrame(() => { setTimeout(() => window.dispatchEvent(new Event('resize')), 60); });
\u003c/script\u003e
</body></html>`;

  return { head, foot };
}

/**
 * Rebuild shell CSS from current tokens and refresh the design iframe.
 * Used by brand panel color editing and chat coordinator after token refinement.
 */
function refreshDesignPreview() {
  const c = ST.concept;
  if (!c) return;
  const pg = curPage();
  const shell = buildDesignShell(c);
  pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
  pg.assembled = assembled(true);
  writeFrame('ifr-design', pg.assembled);
}

// refineDesignTokens, refineDesignVision are pure API logic — direct migration to composables.

/**
 * Refine design tokens via AI — updates CSS values only (routed from "tokens" target).
 * @param {string} refineNote
 * @returns {Promise<string>}
 */
async function refineDesignTokens(refineNote) {
  const c = ST.concept;
  const refineSys = `<role>
You are refining an existing design token system. The user wants a specific change — apply exactly that change.
</role>

<task>
Modify the current design tokens to satisfy the refinement request. Change only the tokens directly affected. Preserve all other tokens exactly as they are.
</task>

<rules>
- Scope changes tightly: "make CTA red" → change only the accent color. "Use Playfair Display for headings" → change only the heading font. Unrelated tokens stay as-is.
- Avoid cascading: changing one color does not require changing others "to match" — the design system was already coherent.
- Contrast check: if changing a background color, verify text colors still have 4.5:1 contrast. If not, adjust only the conflicting text color.
- Return the complete token set (all fields), with minimal modifications.
</rules>

<example>
Request: "make the CTA button orange"
Change: accent color → orange hex. All other tokens unchanged.
</example>`;

  const result = await claude({
    sys: refineSys,
    user: `<current_tokens>\n${JSON.stringify(
      c.designTokens || c.colors,
      null,
      2,
    )}\n</current_tokens>\n\n<refinement_request>${refineNote}</refinement_request>`,
    tokens: 3000,
    model: 'claude-sonnet-4-6',
    temp: 0.2,
    effort: 'medium',
    schema: KEEP_TOKENS_SCHEMA,
  });
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    ST.concept.designTokens = parsed.designTokens || parsed;
    syncFlatTokens(ST.concept);
    ST.cachedTokenCtx = '';
    return parsed.summary || 'Updated tokens';
  } catch (te) {
    console.error('Failed to parse updated tokens:', te);
    return '';
  }
}

/**
 * Refine design vision via AI — updates vision, tokens, and affected section visualBriefs.
 * @param {string} refineNote
 * @param {import('../types.ts').UploadedImage[]} [imgs]
 * @returns {Promise<string>}
 */
async function refineDesignVision(refineNote, imgs = []) {
  const c = ST.concept;
  const raw = await claude({
    sys: `<role>You are updating a website's design direction.</role>
<task>
The user wants to change the design direction. Update:
1. designVision — the overall visual concept, page flow, and signature element
2. designTokens — re-derive ALL token values from the updated vision (colors, fonts, spacing, effects, animation)
3. sectionUpdates — update visualBriefs for sections affected by the direction change

If only some aspects of the vision change, preserve the rest. Re-derive tokens to match the new vision — every token should trace back to the updated design concept.
</task>`,
    user: `<current_vision>\n${JSON.stringify(c.designVision)}\n</current_vision>
<current_tokens>\n${JSON.stringify(c.designTokens)}\n</current_tokens>
<sections>\n${JSON.stringify(c.sections.map(s => ({ id: s.id, type: s.type, visualBrief: s.visualBrief })))}\n</sections>
<refinement>${refineNote}</refinement>`,
    imgs,
    model: 'claude-sonnet-4-6',
    tokens: 5000,
    temp: 0.3,
    effort: 'medium',
    schema: VISION_REFINE_SCHEMA,
  });
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    // Apply vision
    c.designVision = parsed.designVision;
    // Apply re-derived tokens
    c.designTokens = parsed.designTokens;
    syncFlatTokens(c);
    // Apply visualBrief updates — sync to BOTH concept sections AND page sections
    for (const update of parsed.sectionUpdates || []) {
      const cSect = c.sections.find(s => s.id === update.id);
      if (cSect) {
        cSect.visualBrief = update.visualBrief;
      }
      for (const pg of ST.pages) {
        const pgSect = pg.sects.find(s => s.id === update.id);
        if (pgSect) {
          pgSect.visualBrief = update.visualBrief;
          if (pgSect.status === 'done') {
            pgSect.stale = true;
          }
        }
      }
    }
    ST.cachedTokenCtx = '';
    return parsed.summary || 'Updated design vision';
  } catch (te) {
    console.error('Failed to parse vision refinement:', te);
    return '';
  }
}

// ── DESIGN PREVIEW — builds on real page ─────────────────────────
async function GenDesignPreview() {
  if (designBusy) {
    G.go('design');
    return;
  }
  designBusy = true;
  G.go('design');
  // Force loader visible immediately
  const loader = $('design-loader');
  if (loader) {
    loader.classList.remove('hidden');
  }
  const dlSub = loader?.querySelector('.dl-sub');
  if (dlSub) {
    dlSub.textContent = 'Waiting for stock images\u2026';
  }

  try {
    // Wait for stock image search if still in progress
    if (ST.stockPromise) {
      await ST.stockPromise;
      ST.stockPromise = null;
    }

    await skillsReady;

    const c = ST.concept;
    const pg = ST.pages[0];

    // Reset caches
    ST.cachedTokenCtx = '';
    ST.cachedSkillCtx = {};
    ST.cachedBusinessCtx = '';

    const shell = buildDesignShell(c);
    pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
    pg.shellStatus = 'done';

    // Select 3 subset sections for design preview (header + hero + one content section)
    const allSections = pg.sects;
    const headerSect = allSections.find(s => s.type === 'header');
    const heroSect = allSections.find(s => s.type === 'hero');
    const preferTypes = ['features', 'services', 'stats', 'process', 'social-proof', 'about'];
    const skipTypes = [
      'header',
      'hero',
      'footer',
      'cta',
      'trust-ticker',
      'faq',
      'contact',
      'subscribe',
      'video',
      'content',
    ];
    const contentSect =
      allSections.find(s => preferTypes.includes(s.type)) ||
      allSections.find(s => !skipTypes.includes(s.type));
    const previewSects = [headerSect, heroSect, contentSect].filter(Boolean);

    ST.uploadImgMap = buildUploadImgMap();

    let loaderHidden = false;
    const sectsToBuild = previewSects.filter(s => s.status === 'pending');

    for (let i = 0; i < allSections.length; i++) {
      const sect = allSections[i];
      if (!previewSects.includes(sect)) {
        continue;
      }
      if (sect.status === 'done') {
        continue;
      }

      const buildIdx = sectsToBuild.indexOf(sect) + 1;
      if (dlSub) {
        dlSub.textContent = `Building ${sect.type}\u2026 (${buildIdx}/${sectsToBuild.length})`;
      }

      await buildSect(i, pg, {
        previewFrame: 'ifr-design',
        skipStatusRender: true,
        skipSync: true,
        onSectionDone: () => {
          if (!loaderHidden) {
            const loaderEl = $('design-loader');
            if (loaderEl) {
              loaderEl.classList.add('hidden');
            }
            loaderHidden = true;
          }
        },
      });
    }

    designBusy = false;
    pg.assembled = assembledForPage(pg);
    finalizeFrame('ifr-design', pg.assembled);
    renderDesign();
  } catch (e) {
    designBusy = false;
    renderDesign();
    console.error('Design preview failed:', e);
    showErr(`Design preview failed: ${e.message}`);
  }
}

const { renderTabs: renderDesignTabs, renderContent: renderDesignTabContent } = createTabSwitcher({
  barId: 'design-tabs',
  stateKey: 'designTab',
  tabs: [
    { id: 'brand', label: 'Brand', contentId: 'design-brand-content', render: renderBrandPanel },
    { id: 'chat', label: 'Chat', contentId: 'design-chat-content', render: () => renderChat('design-chat', 'ifr-design') },
  ],
});

function renderDesign() {
  renderDevSw('dsw-design');
  const ctx = $('design-ctx');
  if (ctx) {
    ctx.textContent = designBusy
      ? 'Generating design preview\u2026'
      : ST.concept?.siteName || 'Design preview';
  }
  // Both continue buttons
  const continueBtn = /** @type {HTMLButtonElement | null} */ ($('design-continue-btn'));
  const continueBtn2 = /** @type {HTMLButtonElement | null} */ ($('design-continue-btn2'));
  if (continueBtn) continueBtn.disabled = designBusy;
  if (continueBtn2) continueBtn2.disabled = designBusy;
  // Toggle loading overlay
  const loader = $('design-loader');
  if (loader) {
    if (designBusy && !curPage().assembled) {
      loader.classList.remove('hidden');
    } else {
      loader.classList.add('hidden');
    }
  }
  // Tabs + content
  renderDesignTabs();
  renderDesignTabContent();

  if (curPage().assembled && !designBusy) {
    setTimeout(() => {
      writeFrame('ifr-design', curPage().assembled);
    }, 40);
  }
}

// ── MODULE EXPORTS ──────
export {
  refineDesignTokens,
  refineDesignVision,
  refreshDesignPreview,
  buildDesignShell,
  genDesignPreview,
  renderDesign,
};
